import React, { useState } from 'react';
import { Modal, ActionCard, Switch, Button, BlockCard, Textarea, showToast } from '@trellixio/roaster-coffee';
import PropTypes from 'prop-types';
import jwtEncode from 'jwt-encode';
import { jwtDecode } from 'jwt-decode';
import { getUserName, formatDate } from 'helpers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPI, useGetPilgrim, useGetUser } from 'hooks';
import Locales from 'locales';
import style from './Trips.module.css';

const MedicalHistoryCard = ({ healthNotes }) => {
  let logs = [];

  if (healthNotes) {
    const decoded = jwtDecode(healthNotes);
    logs = Array.isArray(decoded) ? decoded : [decoded];
  }

  return (
    <div>
      {logs.length > 0 &&
        logs.map((decodedLog) => (
          <div key={decodedLog.checkupDate} className={style.logEntry}>
            <p>
              {decodedLog.checkupDate
                ? Locales.formatString(Locales.trip.medical_certificate.medical_checkup_date, {
                    date: <strong>{decodedLog.checkupDate}</strong>,
                  })
                : ''}
            </p>
            <div className={style.logSection}>
              <p className={style.logItem}>
                <strong>{Locales.trip.title.slice(0, -1)} :</strong> {decodedLog.pilgrim.first_name}{' '}
                {decodedLog.pilgrim.last_name}
              </p>
              <p className={style.logItem}>
                <strong>{Locales.trip.table.doctor} :</strong> {decodedLog.doctorName} ({decodedLog.doctorEmail})
              </p>
              <p className={style.logItem}>
                <strong>{Locales.trip.table.status} : </strong>
                <span className={decodedLog.checkupStatus === 'Apte' ? style.statusApte : style.statusInapte}>
                  {decodedLog.checkupStatus}
                </span>
              </p>
              <p className={style.logItem}>
                <strong>{Locales.trip.medical_certificate.notes} :</strong> {decodedLog.userNote}
              </p>
            </div>
            {logs.indexOf(decodedLog) < logs.length - 1 && <hr className={style.separator} />}
          </div>
        ))}
    </div>
  );
};

MedicalHistoryCard.defaultProps = {
  healthNotes: null,
};

MedicalHistoryCard.propTypes = {
  healthNotes: PropTypes.string,
};

const MedicalCheckupModal = ({ isOpened, closeModal, pilgrimData }) => {
  const {
    id,
    firstname,
    lastname,
    npi,
    note,
    passport,
    birthdate,
    health,
    categoryId,
    isPassportValid,
    passportExpirationDate,
    isInternational,
    isApprovedForMSStep,
  } = useGetPilgrim(pilgrimData);

  const { user, isMSSecondary, isMSPrimary } = useGetUser();

  const { api } = useAPI();
  const queryClient = useQueryClient();
  const isMutating = queryClient.isMutating() > 0;

  const disabledForMSSecondary = isMSSecondary && isApprovedForMSStep !== null;

  const initialHealthData = {
    q1_is_vax_covid: health.q1_is_vax_covid || false,
    q2_is_vax_meningitis: health.q2_is_vax_meningitis || false,
    q3_is_vax_yellow: health.q3_is_vax_yellow || false,
    q4_is_vax_poliomyelitis: health.q4_is_vax_poliomyelitis || false,
    q6_is_vax_flu: health.q6_is_vax_flu || false,
    q7a_is_issue_psychomotor: health.q7a_is_issue_psychomotor || false,
    q7b_is_issue_locomotor: health.q7b_is_issue_locomotor || false,
    q7c_is_issue_seeing: health.q7c_is_issue_seeing || false,
    q7d_is_issue_chronic: health.q7d_is_issue_chronic || false,
    q7e_is_chronic_treatment: health.q7e_is_chronic_treatment || false,
  };

  // Define local conditions for each health issue
  const [q1IsVaxCovid, setQ1IsVaxCovid] = useState(health.q1_is_vax_covid || false);
  const [q2IsVaxMeningitis, setQ2IsVaxMeningitis] = useState(health.q2_is_vax_meningitis || false);
  const [q3IsVaxYellow, setQ3IsVaxYellow] = useState(health.q3_is_vax_yellow || false);
  const [q4IsVaxPoliomyelitis, setQ4IsVaxPoliomyelitis] = useState(health.q4_is_vax_poliomyelitis || false);
  const [q6IsVaxFlu, setQ6IsVaxFlu] = useState(health.q6_is_vax_flu || false);
  const [q7aIsIssuePsychomotor, setQ7aIsIssuePsychomotor] = useState(health.q7a_is_issue_psychomotor || false);
  const [q7bIsIssueLocomotor, setQ7bIsIssueLocomotor] = useState(health.q7b_is_issue_locomotor || false);
  const [q7cIsIssueSeeing, setQ7cIsIssueSeeing] = useState(health.q7c_is_issue_seeing || false);
  const [q7dIsIssueChronic, setQ7dIsIssueChronic] = useState(health.q7d_is_issue_chronic || false);
  const [q7eIsChronicTreatment, setQ7eIsChronicTreatment] = useState(health.q7e_is_chronic_treatment || false);
  const [healthNote, setHealthNote] = useState('');

  const [isHistoryOpened, setIsHistoryOpened] = useState(false);

  const handleNoteChange = (value) => {
    setHealthNote(value);
  };

  const checkHealthStatus = (healthData) => {
    return (
      healthData.q1_is_vax_covid &&
      healthData.q2_is_vax_meningitis &&
      healthData.q3_is_vax_yellow &&
      healthData.q4_is_vax_poliomyelitis &&
      healthData.q6_is_vax_flu &&
      !healthData.q7a_is_issue_psychomotor &&
      !healthData.q7b_is_issue_locomotor &&
      !healthData.q7c_is_issue_seeing &&
      !healthData.q7d_is_issue_chronic &&
      !healthData.q7e_is_chronic_treatment
    );
  };

  const pilgrim = {
    first_name: firstname,
    last_name: lastname,
    npi,
    passport,
    birthdate: formatDate(birthdate, 'YYYY-MM-DD'),
    is_passport_valid: isPassportValid,
    passport_expiration: passportExpirationDate ? formatDate(passportExpirationDate, 'YYYY-MM-DD') : null,
    is_international: isInternational,
  };

  const healthData = {
    q1_is_vax_covid: q1IsVaxCovid,
    q2_is_vax_meningitis: q2IsVaxMeningitis,
    q3_is_vax_yellow: q3IsVaxYellow,
    q4_is_vax_poliomyelitis: q4IsVaxPoliomyelitis,
    q6_is_vax_flu: q6IsVaxFlu,
    q7a_is_issue_psychomotor: q7aIsIssuePsychomotor,
    q7b_is_issue_locomotor: q7bIsIssueLocomotor,
    q7c_is_issue_seeing: q7cIsIssueSeeing,
    q7d_is_issue_chronic: q7dIsIssueChronic,
    q7e_is_chronic_treatment: q7eIsChronicTreatment,
  };

  let previousLogs = [];

  if (health.notes) {
    const decodedNotes = jwtDecode(health.notes);
    previousLogs = Array.isArray(decodedNotes) ? decodedNotes : [decodedNotes];
  }

  // Generate an initial retroactive log if no logs exist and health status is known
  if (isApprovedForMSStep !== null && (!health.notes || health.notes.trim() === '') && previousLogs.length === 0) {
    const initialLog = {
      pilgrim,
      health: { ...initialHealthData },
      doctorEmail: user.email,
      doctorName: getUserName(user.first_name, user.last_name),
      checkupDate: null,
      checkupStatus: checkHealthStatus(initialHealthData) ? 'Apte' : 'Inapte',
      userNote: note,
    };
    previousLogs.push(initialLog);
  }

  const { mutate: medicalCheckup } = useMutation({
    mutationFn: (data) => api.update('trip', data, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trip/'] });
      queryClient.invalidateQueries({ queryKey: ['trip/stats/default/'] });
      showToast({
        message: Locales.formatString(Locales.trip.toast.pilgrim_updated, {
          fullname: getUserName(firstname, lastname),
        }),
        level: 'success',
      });
      closeModal();
    },
  });

  const handleMedicalCheckup = () => {
    // Only create a new log entry if there have been modifications
    const healthLog = {
      pilgrim,
      health: { ...healthData },
      doctorEmail: user.email,
      doctorName: getUserName(user.first_name, user.last_name),
      checkupDate: formatDate(new Date(), 'dddd DD MMMM YYYY [à] HH:mm:ss'),
      checkupStatus: checkHealthStatus(healthData) ? 'Apte' : 'Inapte',
      userNote: healthNote,
    };

    const updatedLogs = [...previousLogs, healthLog];
    const updatedToken = jwtEncode(updatedLogs, process.env.REACT_APP_JWT_SECRET);

    const updatedPilgrim = {
      pilgrim,
      health: { ...healthData, notes: updatedToken },
      note,
      category_id: categoryId,
    };

    medicalCheckup(updatedPilgrim);
  };

  const getUpdatedLogs = () => {
    return jwtEncode(previousLogs, process.env.REACT_APP_JWT_SECRET);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      setIsHistoryOpened((val) => !val);
    }
  };

  return (
    <div className={style.medicalform}>
      <Modal
        title={Locales.trip.modal.title.certificate}
        opened={isOpened}
        onClose={closeModal}
        size="large"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              {Locales.base.button.cancel}
            </Button>
            <Button
              variant="primary"
              color="success"
              onClick={handleMedicalCheckup}
              disabled={isMutating || disabledForMSSecondary}
            >
              {Locales.base.button.validate}
            </Button>
          </>
        }
      >
        <form>
          <BlockCard>
            <p>
              {Locales.trip.medical_certificate.establishment}
              <strong>{getUserName(firstname, lastname)} :</strong>
            </p>
            <h3>{Locales.trip.medical_certificate.vaccination}</h3>
            <ActionCard
              actions={
                <Switch defaultChecked={q1IsVaxCovid} onChange={setQ1IsVaxCovid} disabled={disabledForMSSecondary} />
              }
            >
              <h4>{Locales.trip.medical_certificate.coronavirus}</h4>
              <p>{Locales.trip.medical_certificate.covid19_vaccine}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q2IsVaxMeningitis}
                  onChange={setQ2IsVaxMeningitis}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.meningitis}</h4>
              <p>{Locales.trip.medical_certificate.meningitis_vaccine}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch defaultChecked={q3IsVaxYellow} onChange={setQ3IsVaxYellow} disabled={disabledForMSSecondary} />
              }
            >
              <h4>{Locales.trip.medical_certificate.yellow_fever}</h4>
              <p>{Locales.trip.medical_certificate.yellow_fever_vaccine}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q4IsVaxPoliomyelitis}
                  onChange={setQ4IsVaxPoliomyelitis}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.polio}</h4>
              <p>{Locales.trip.medical_certificate.polio_vaccine}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch defaultChecked={q6IsVaxFlu} onChange={setQ6IsVaxFlu} disabled={disabledForMSSecondary} />
              }
            >
              <h4>{Locales.trip.medical_certificate.flu}</h4>
              <p>{Locales.trip.medical_certificate.flu_vaccine}</p>
            </ActionCard>
            <hr /> <h3>{Locales.trip.medical_certificate.physical_exam}</h3>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q7aIsIssuePsychomotor}
                  onChange={setQ7aIsIssuePsychomotor}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.psychomotor_disorders}</h4>
              <p>{Locales.trip.medical_certificate.psychomotor_disorders_description}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q7bIsIssueLocomotor}
                  onChange={setQ7bIsIssueLocomotor}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.locomotor_disorders}</h4>
              <p>{Locales.trip.medical_certificate.locomotor_disorders_description}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q7cIsIssueSeeing}
                  onChange={setQ7cIsIssueSeeing}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.vision_disorders}</h4>
              <p>{Locales.trip.medical_certificate.vision_disorders_description}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q7dIsIssueChronic}
                  onChange={setQ7dIsIssueChronic}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.chronic_disease}</h4>
              <p>{Locales.trip.medical_certificate.chronic_disease_description}</p>
            </ActionCard>
            <ActionCard
              actions={
                <Switch
                  defaultChecked={q7eIsChronicTreatment}
                  onChange={setQ7eIsChronicTreatment}
                  disabled={disabledForMSSecondary}
                />
              }
            >
              <h4>{Locales.trip.medical_certificate.chronic_disease_treatment}</h4>
              <p>{Locales.trip.medical_certificate.chronic_disease_treatment_description}</p>
            </ActionCard>
            {isMSPrimary && isApprovedForMSStep !== null && (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsHistoryOpened(!isHistoryOpened)}
                  onKeyDown={(e) => handleKeyDown(e)}
                  className={style.collapsible}
                >
                  <p className={style.collapsibleTitle}>{Locales.trip.medical_certificate.history}</p>
                  <i className={`fa-solid fa-chevron-down ${style.chevron} ${isHistoryOpened ? style.rotated : ''}`} />
                </div>
                {isHistoryOpened && (
                  <div className={style.history}>
                    <MedicalHistoryCard healthNotes={getUpdatedLogs()} />
                  </div>
                )}
              </>
            )}
            <Textarea
              label={Locales.trip.medical_certificate.notes}
              placeholder={Locales.trip.medical_certificate.notes_description}
              onChange={handleNoteChange}
            />
          </BlockCard>
        </form>
      </Modal>
    </div>
  );
};

MedicalCheckupModal.defaultProps = {
  isOpened: false,
  pilgrimData: null,
  closeModal: null,
};

MedicalCheckupModal.propTypes = {
  isOpened: PropTypes.bool,
  pilgrimData: PropTypes.shape({}),
  closeModal: PropTypes.func,
};

export default MedicalCheckupModal;
